import { format, isToday, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { CalendarEvent } from '@/services/calendarService';
import EventCard from './EventCard';
import { useRef, useEffect } from 'react';

interface CalendarDayViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  selectedDate: Date | null;
  onDateClick: (date: Date) => void;
}

export default function CalendarDayView({
  currentDate,
  events,
  selectedDate,
  onDateClick,
}: CalendarDayViewProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Scroll all'ora corrente al mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      const now = new Date();
      const currentHour = now.getHours();
      const hourElement = scrollContainerRef.current.querySelector(`[data-hour="${currentHour}"]`);
      if (hourElement) {
        hourElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentDate]);

  const getEventsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => {
      const eventDate = format(new Date(event.start), 'yyyy-MM-dd');
      return eventDate === dateStr;
    });
  };

  const getHourEvents = (hour: number) => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    return events.filter(event => {
      if (event.allDay) return false;
      const eventDate = new Date(event.start);
      const eventHour = eventDate.getHours();
      const eventDateStr = format(eventDate, 'yyyy-MM-dd');
      return eventDateStr === dateStr && eventHour === hour;
    });
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const dayEvents = getEventsForDate(currentDate);
  const allDayEvents = dayEvents.filter(e => e.allDay);
  const isDayToday = isToday(currentDate);
  const isSelected = selectedDate && isSameDay(currentDate, selectedDate);

  return (
    <div className="space-y-4">
      {/* Header giorno */}
      <div className={`text-center p-4 rounded-lg border-2 ${
        isDayToday ? 'bg-blue-50 border-blue-500' :
        isSelected ? 'bg-blue-50 border-blue-300' :
        'bg-gray-50 border-gray-200'
      }`}>
        <div className="text-sm text-gray-500 mb-1">
          {format(currentDate, 'EEEE', { locale: it })}
        </div>
        <div className={`text-3xl font-bold ${isDayToday ? 'text-blue-600' : 'text-gray-900'}`}>
          {format(currentDate, 'd')}
        </div>
        <div className="text-sm text-gray-600 mt-1">
          {format(currentDate, 'MMMM yyyy', { locale: it })}
        </div>
        <div className="text-xs text-gray-500 mt-2">
          {dayEvents.length} {dayEvents.length === 1 ? 'evento' : 'eventi'}
        </div>
      </div>

      {/* All-day events */}
      {allDayEvents.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm font-semibold text-gray-700">Eventi tutto il giorno</div>
          {allDayEvents.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </div>
      )}

      {/* Griglia oraria con scroll solo ore */}
      <div 
        ref={scrollContainerRef}
        className="border rounded-lg overflow-y-auto"
        style={{ maxHeight: '600px' }}
      >
        <div className="space-y-0">
          {hours.map((hour) => {
            const hourEvents = getHourEvents(hour);
            
            return (
              <div
                key={hour}
                data-hour={hour}
                className={`flex border-b last:border-b-0 min-h-[80px] ${
                  isDayToday && new Date().getHours() === hour ? 'bg-blue-50' : 'bg-white'
                }`}
              >
                <div className="w-20 shrink-0 border-r p-2 text-xs text-gray-500 font-medium">
                  {format(new Date().setHours(hour, 0, 0, 0), 'HH:mm')}
                </div>
                <div className="flex-1 p-2">
                  {hourEvents.length === 0 ? (
                    <div className="text-xs text-gray-400 py-4">Nessun evento</div>
                  ) : (
                    <div className="space-y-2">
                      {hourEvents.map((event) => (
                        <div
                          key={event.id}
                          className={`text-xs p-2 rounded ${
                            event.type === 'appointment' 
                              ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                              : 'bg-red-100 text-red-800 border border-red-200'
                          }`}
                          title={event.title}
                        >
                          <div className="font-medium">{event.title}</div>
                          {event.description && (
                            <div className="text-xs opacity-75 mt-1">{event.description}</div>
                          )}
                          {event.client && (
                            <div className="text-xs opacity-75 mt-1">Cliente: {event.client}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


